/* eslint-disable max-len */
import { Router } from '@angular/router';
import { delay, map, retry, retryWhen } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { EventsService } from './events.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments';
import { AuthenticationService, LoginRequest } from '@ews/users-management';
import { Request } from './../model/request';

@Injectable({
  providedIn: 'root',
})
export class RestService {
  baseUrl = environment.apiHost + '';

  // globals
  public assignedCompany: string;
  public assignedDepartment: string;

  public lastCompany: string;
  public lastDepartment: string;
  public lastDepartmentId: string;
  public lastProject: string;
  public usernameSubject = new BehaviorSubject<string>('');
  public emailSubject = new BehaviorSubject<string>('');
  public username$ = this.usernameSubject.asObservable();
  public email$ = this.emailSubject.asObservable();
  public username: string;
  public headers: HttpHeaders;

  private dbUsername = '';

  constructor(
    private http: HttpClient,
    private events: EventsService,
    private auth: AuthenticationService,
    private router: Router
  ) {
    this.headers = new HttpHeaders({
      // eslint-disable-next-line max-len, @typescript-eslint/naming-convention
      Authorization:
        'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6InN0b3Bfd2F0Y2hpbmciLCJpYXQiOjE2MTkyMzkwMjJ9.98Rj9knuCBtc5eV63n2Ms0cg8V2wnIB8FyOrcZAqQyc',
    });
  }

  updateUsername(username: string) {
    this.usernameSubject.next(username);
  }

  updateEmail(email: string) {
    this.emailSubject.next(email);
  }

  public loginAfterUserManagement(
    username: string,
    userManagementId: string
  ): Observable<boolean> {
    return this.http
      .post(this.baseUrl + 'authentication/usermanagement', {
        username: username || 'username',
        userManagementId: userManagementId || 'password',
      })
      .pipe(
        map((response: any) => {
          this.username = response.authresult.username;
          this.usernameSubject.next(this.username);
          console.log('rest.username=', this.username);
          return true;
        })
      );
  }

  logout(): void {
    this.username = null;
    this.usernameSubject.next('');
  }

  logoutNew(): void {
    this.auth.logout().subscribe((result) => {
      console.log('logged out');
      this.logout();
      this.router.navigate(['login']);
    });
  }

  async loginTemp(logindata) {
    const urlitems = this.baseUrl + 'authentication';
    const response = await this.http.post(urlitems, logindata).toPromise();
    return response;
  }

  async getOrders() {
    const url = this.baseUrl + 'orders';

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getProcessingLines(): Promise<any[]> {
    const url =
      this.baseUrl +
      'test/configuration/select?value=Dictionary, Planning lines';

    const response = await this.http
      .get<any[]>(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getOrder(id: number) {
    const url = this.baseUrl + 'orders/' + id;

    const response = this.http.get(url, { headers: this.headers }).toPromise();
    return response;
  }

  async getOrderProcessingSheet(id: number) {
    const url = this.baseUrl + 'orders/' + id + '/processing-sheet';

    const response = this.http.get(url, { headers: this.headers }).toPromise();
    return response;
  }

  async getOrderProcessingDetails(id: number, line: string) {
    const url = `${this.baseUrl}orders/processing-details`;
    const params = {
      id,
      line,
    };
    const response = this.http
      .get(url, { headers: this.headers, params })
      .toPromise();
    return response;
  }

  saveWeightRegistration(
    orderId: number,
    line: string,
    data: {
      lotNumber: string;
      palletNumber: number;
      outgoingPackaging: string;
      outgoingPackagingAmount: number;
      weight: number;
      qis: boolean;
    }
  ) {
    const url = `${this.baseUrl}orders/processing-details/register-weight`;
    const timestamp = new Date().toISOString();
    return this.http
      .post(
        url,
        { ...data, orderId, line, timestamp },
        { headers: this.headers }
      )
      .toPromise();
  }

  saveWasteRegistration(
    orderId: number,
    line: string,
    data: {
      source: string;
      sieveSize: string | null;
      otherKg: number;
      findings: string;
      photos: Array<{ dataUrl: string; fileName: string; size: number }>;
    }
  ) {
    const url = `${this.baseUrl}orders/processing-details/register-waste`;
    const timestamp = new Date().toISOString();
    return this.http
      .post(
        url,
        { ...data, orderId, line, timestamp },
        { headers: this.headers }
      )
      .toPromise();
  }

  updateProcessingStatus(
    orderId: number,
    line: string,
    status: 'start' | 'pause' | 'resume' | 'finalized'
  ) {
    const url = `${this.baseUrl}orders/processing-details/status`;
    return this.http
      .patch(url, { orderId, line, status }, { headers: this.headers })
      .toPromise();
  }

  async blockOrder(orderId: number, block: boolean, remarks: string) {
    const url = this.baseUrl + 'orders/block-processing';

    const response = this.http
      .post(
        url,
        { id: orderId, blocked: block, blockedRemarks: remarks },
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getGasDefinitions(id: number) {
    const url = this.baseUrl + 'test/orders/' + id + '/gas/definitions';

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getGasMeasurements(id: number, gasStructure: string) {
    const url = this.baseUrl + 'test/orders/' + id + '/gas/' + gasStructure;

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async saveGasMeasurements(id: number, json: any) {
    const url = this.baseUrl + 'test/orders/' + id + '/gas';

    console.log('REST SAVING (' + url + ') : ' + JSON.stringify(json));

    this.http
      .patch(url, json, { headers: this.headers })
      .toPromise()
      .then((data) => {
        console.log(data);
        this.events.itemSaved.emit(true);
      });
  }

  async getCategories() {
    const url = this.baseUrl + 'questions/categories';

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getQuestions() {
    const url = this.baseUrl + 'questions/ALL';

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async saveAnswers(
    id: number,
    json: any,
    questions: any,
    questionsWithAnswers: any
  ) {
    /*
    let headers = new Headers();
    headers.append("Accept" , 'application/json');
    headers.append('Content-Type', 'application/json');
    let options = new RequestOptions({ headers: headers });
*/
    const url = this.baseUrl + 'questions/v2/' + id;

    console.log('REST SAVING (' + url + ') : ' + JSON.stringify(json));

    this.http
      .patch(
        url,
        {
          answers: json,
          questions,
          questionsWithAnswers,
          formauthor: this.username,
        },
        { headers: this.headers }
      )
      .toPromise()
      .then((data) => {
        console.log(data);
        this.events.itemSaved.emit(true);
      });
  }

  async saveOrderMobile(id: number, json: any) {
    const url = this.baseUrl + 'orders/' + id + '/mobile';

    console.log('REST SAVING MOBILE (' + url + ') : ' + JSON.stringify(json));

    this.http
      .patch(url, json, { headers: this.headers })
      .toPromise()
      .then((data) => {
        console.log(data);
        this.events.itemSavedOrder.emit(true);
      });
  }

  async savePhoto(id: number, photo: string) {
    const url = this.baseUrl + 'questions/photos/' + id;

    console.log('REST SAVING (' + url + ') : ' + photo);

    this.http
      .patch(url, { picture: photo }, { headers: this.headers })
      .toPromise()
      .then((data) => {
        console.log(data);
        this.events.itemCreated.emit(true);
      });
  }

  async deletePhoto(id: number) {
    const url = this.baseUrl + 'questions/photos/' + id;

    const response = this.http
      .delete(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getPhotos(id: number) {
    const url = this.baseUrl + 'questions/photos/' + id;

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async removePhoto(id: number) {
    const url = this.baseUrl + 'questions/photos/' + id;

    const response = await this.http
      .delete(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async getAnswers(id: number) {
    const url = this.baseUrl + 'questions/answers/' + id;

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  async authUser(key: string) {
    const url = this.baseUrl + 'authmobile/' + key;

    const response = await this.http
      .get(url, { headers: this.headers })
      .toPromise();
    return response;
  }

  getUsername() {
    return this.username;
  }

  getDBUserName() {
    return this.dbUsername;
  }

  saveQuestionnaireAnswers(
    type: string,
    answerId: number,
    json: any
  ): Promise<any> {
    return this.http
      .post(
        this.baseUrl + 'questionaire/' + type + '/answer/' + answerId,
        json,
        {
          headers: this.headers,
        }
      )

      .toPromise();
  }
  getSaveQuestionnaireAnswersOfflineRequest(
    type: string,
    answerId: number,
    json: any
  ): Request {
    return new Request(
      'POST',
      this.baseUrl + 'questionaire/' + type + '/answer/' + answerId,
      json
    );
  }

  deleteQuestionnairePhoto(type: string, photoId): Promise<any> {
    return this.http
      .delete(
        this.baseUrl + 'questionaire/' + type + '/answer/photo/' + photoId,
        {
          headers: this.headers,
        }
      )

      .toPromise();
  }

  getDeleteQuestionnairePhotoOfflineRequest(type: string, photoId): Request {
    return new Request(
      'DELETE',
      this.baseUrl + 'questionaire/' + type + '/answer/photo/' + photoId,
      ''
    );
  }

  saveQuestionnairePhoto(
    type: string,
    answerId: number,
    json: any
  ): Promise<any> {
    return this.http
      .put(
        this.baseUrl +
          'questionaire/' +
          type +
          '/answer/' +
          answerId +
          '/photo',
        json,
        { headers: this.headers }
      )

      .toPromise();
  }

  getSaveQuestionnairePhotoOfflineRequest(
    type: string,
    answerId: number,
    json: any
  ): Request {
    return new Request(
      'PUT',
      this.baseUrl + 'questionaire/' + type + '/answer/' + answerId + '/photo',
      json
    );
  }

  updateQuestionnairePhoto(
    type: string,
    photoId: number,
    json: any
  ): Promise<any> {
    return this.http
      .patch(
        this.baseUrl + 'questionaire/' + type + '/answer/photo/' + photoId,
        json,
        { headers: this.headers }
      )

      .toPromise();
  }

  getUpdateQuestionnairePhotoOfflineRequest(
    type: string,
    photoId: number,
    json: any
  ): Request {
    return new Request(
      'PATCH',
      this.baseUrl + 'questionaire/' + type + '/answer/photo/' + photoId,
      json
    );
  }

  deleteQuestionnaireComment(type: string, commentId): Promise<any> {
    return this.http
      .delete(
        this.baseUrl + 'questionaire/' + type + '/answer/comment/' + commentId,
        { headers: this.headers }
      )

      .toPromise();
  }

  getDeleteQuestionnaireCommentOfflineRequest(
    type: string,
    commentId
  ): Request {
    return new Request(
      'DELETE',
      this.baseUrl + 'questionaire/' + type + '/answer/comment/' + commentId,
      ''
    );
  }

  saveQuestionnaireComment(
    type: string,
    answerId: number,
    json: any
  ): Promise<any> {
    return this.http
      .put(
        this.baseUrl +
          'questionaire/' +
          type +
          '/answer/' +
          answerId +
          '/comment',
        json,
        { headers: this.headers }
      )

      .toPromise();
  }

  updateQuestionnaireComment(
    type: string,
    commentId: number,
    json: any
  ): Promise<any> {
    return this.http
      .patch(
        this.baseUrl + 'questionaire/' + type + '/answer/comment/' + commentId,
        json,
        { headers: this.headers }
      )

      .toPromise();
  }

  getUpdateQuestionnaireCommentOfflineRequest(
    type: string,
    commentId: number,
    json: any
  ): Request {
    return new Request(
      'PATCH',
      this.baseUrl + 'questionaire/' + type + '/answer/comment/' + commentId,
      json
    );
  }

  getSaveQuestionnaireCommentOfflineRequest(
    type: string,
    answerId: number,
    json: any
  ): Request {
    return new Request(
      'PUT',
      this.baseUrl +
        'questionaire/' +
        type +
        '/answer/' +
        answerId +
        '/comment',
      json
    );
  }

  getQuestionnaire(
    parentId: number,
    parentObject: string,
    customFormId: number,
    questionnaireId: number | null = null
  ): Promise<any> {
    const url = `${this.baseUrl}questionaire/${parentObject}/${parentId}/mobile`;
    const params: any = { customFormId };

    if (questionnaireId !== null) {
      params.questionnaireId = questionnaireId;
    }

    return this.http
      .get<any>(url, {
        params,
        headers: this.headers,
      })
      .toPromise();
  }

  getQuestionnairePhotos(type: string, answerId: number): Promise<any> {
    return this.http
      .get<any>(this.baseUrl + 'questionaire/' + type + '/photo/' + answerId, {
        headers: this.headers,
      })

      .toPromise();
  }

  getQuestionnaireComments(type: string, answerId: number): Promise<any> {
    return this.http
      .get<any>(
        this.baseUrl + 'questionaire/' + type + '/comment/' + answerId,
        {
          headers: this.headers,
        }
      )

      .toPromise();
  }

  putPendingRequest(request: Request): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (request.requestType === 'PUT') {
        this.http
          .put(request.url, request.body, { headers: this.headers })
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else if (request.requestType === 'POST') {
        this.http
          .post(request.url, request.body, { headers: this.headers })
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else if (request.requestType === 'DELETE') {
        this.http
          .delete(request.url, { headers: this.headers })
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      }
    });
  }
}
